import React from "react";

import { useParams } from "react-router";
import { Navigate } from "react-router-dom";

import { Box, Container, Stack, Typography } from "@mui/material";

import Tabs, { Tab } from "@app/analytics/components/Tabs";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import ROUTES from "@app/common/routes";
import { withQueryParamsUrl } from "@app/common/utils";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { usePageTitle } from "@app/utils/hooks";

import { Row } from "@rui/Stack";

import DateSelector from "./DateSelector";
import DayOfWeekModule from "./DayOfWeekModule";
import LegendAndToggles from "./LegendAndToggles";
import MeetingDurationModule from "./MeetingDurationModule";
import MeetingPolicyBanner from "./MeetingPolicyBanner";
import { MEETING_POLICY_TABS } from "./meetingPolicyUtils";
import MeetingSizeModule from "./MeetingSizeModule";
import TimeOfDayModule from "./TimeOfDayModule";
import useMeetingPolicy from "./useMeetingPolicy";
import ActionBar from "../components/ActionBar";

const ModuleRow = ({ children }: { children: React.ReactNode }) => (
  <Stack
    direction={{ xxs: "column", lg: "row" }}
    sx={{ width: "100%", pt: 2, pb: 1 }}
    spacing={3}
  >
    {children}
  </Stack>
);

const WorkspaceMeetingPolicy = ({ workspaceId }: { workspaceId: string }) => {
  const tabsWithParameterizedPaths = MEETING_POLICY_TABS.map((t) => ({
    ...t,
    path: withQueryParamsUrl(ROUTES.workspaceMeetingPolicyById(workspaceId!), {
      tab: t.id,
    }),
  }));

  const defaultRoute = withQueryParamsUrl(
    ROUTES.workspaceMeetingPolicyById(workspaceId!),
    {
      tab: "meetingScore",
    }
  );

  return (
    <CommonMeetingPolicy
      tabs={tabsWithParameterizedPaths}
      defaultRoute={defaultRoute}
    />
  );
};

const PersonalMeetingPolicy = () => {
  const tabsWithPaths = MEETING_POLICY_TABS.map((t) => ({
    ...t,
    path: withQueryParamsUrl(ROUTES.PERSONAL_MEETING_POLICY, { tab: t.id }),
  }));

  const defaultRoute = ROUTES.PERSONAL_MEETING_POLICY;
  return (
    <CommonMeetingPolicy tabs={tabsWithPaths} defaultRoute={defaultRoute} />
  );
};

const CommonMeetingPolicy = ({
  tabs,
  defaultRoute,
}: {
  tabs: Tab[];
  defaultRoute: string;
}) => {
  const { selectedMetric } = useMeetingPolicy();

  const {
    rangePreset,
    setDateRange,
    toggleState,
    setToggleState,
    noDataCount,
  } = useMeetingPolicy();

  // For now, simple check if there's no data for any of the modules
  const allDataIsEmpty = noDataCount > 0;

  return (
    <Box sx={{ width: "100%", bgcolor: "#FFF", height: "100%", pb: 5 }}>
      <ActionBar>
        <Row spacing={2} alignItems="center">
          <Typography variant="h2">Meeting Policy</Typography>
        </Row>
        <DateSelector
          value={rangePreset.rangePreset}
          onChange={(newRange) => setDateRange(newRange)}
        />
      </ActionBar>
      <Container maxWidth="xl" sx={{ width: "100%", pt: 3 }}>
        {allDataIsEmpty && <MeetingPolicyBanner />}
        <Row>
          <Tabs withSearchParams tabs={tabs} defaultRoute={defaultRoute} />
        </Row>
        <LegendAndToggles
          toggleState={toggleState}
          onToggle={setToggleState}
          selectedMetric={selectedMetric}
        />
        <ModuleRow>
          <DayOfWeekModule />
          <TimeOfDayModule />
        </ModuleRow>
        <ModuleRow>
          <MeetingSizeModule />
          <MeetingDurationModule />
        </ModuleRow>
      </Container>
    </Box>
  );
};

const MeetingPolicyContainer = () => {
  usePageTitle("Meeting Policy");

  const { workspaceId: workspaceIdParam } = useParams();
  const { workspaceId } = useWorkspace();
  const { canAccessWorkspaceRollupReports } = useFeatureAccess();

  if (canAccessWorkspaceRollupReports) {
    // user is in a workspace and can access the workspace meeting policy. if they're already
    // on the right route, keep them there and show the workspace meeting policy content.
    if (workspaceIdParam) {
      return <WorkspaceMeetingPolicy workspaceId={workspaceIdParam} />;
    }

    // otherwise, put them on the right route
    return <Navigate to={ROUTES.workspaceMeetingPolicyById(workspaceId!)} />;
  }

  // user is not in a workspace or cannot access the workspace meeting policy. if they're trying
  // to view the workspace meeting policy, navigate them to their personal meeting policy
  if (workspaceIdParam) {
    return <Navigate to={ROUTES.PERSONAL_MEETING_POLICY} />;
  }

  // otherwise, just show them the personal meeting policy content
  return <PersonalMeetingPolicy />;
};

export default MeetingPolicyContainer;
